import { npsReviewStateSchema } from '@server/domains/lesson-review/dto/res.schema'
import { authenticatedFetch } from '@shared/apis/authenticated-fetch'
import { withRequestTimeout as withNpsRequestTimeout } from '@shared/apis/with-request-timeout'
import { env } from '@shared/config/env'

import type { NpsSkipPayload } from '../ui/nps-survey/nps-survey.types'

export interface NpsPendingResponse {
  classId: number
  tutorName: string
  classEndDatetimeUnix: number
  classStartDatetime: string
  tutorProfileSmallImage: string | null
  tutorProfileLargeImage: string | null
}

export interface NpsSubmitResponse {
  success: boolean
}

const createPodoApiUrl = (path: string) => new URL(path, env.NEXT_PUBLIC_PODO_API_URL)

const createJsonHeaders = (bearerToken: string) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${bearerToken}`,
})

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null

const unwrapPodoBaseResponse = (responseBody: unknown) => {
  if (!isRecord(responseBody)) {
    return null
  }

  return 'data' in responseBody ? (responseBody.data ?? null) : responseBody
}

const isNpsPendingResponse = (value: unknown): value is NpsPendingResponse =>
  isRecord(value) && Number.isSafeInteger(value.classId) && Number(value.classId) > 0

export const getPendingNps = async (bearerToken: string): Promise<NpsPendingResponse | null> => {
  return await withNpsRequestTimeout(async (signal) => {
    const response = await authenticatedFetch(createPodoApiUrl('/api/v1/lesson-review/nps/pending'), {
      method: 'GET',
      headers: createJsonHeaders(bearerToken),
      signal,
    })

    if (!response.ok) {
      return null
    }

    const body: unknown = await response.json()
    if (isRecord(body) && Number(body.resultCd) >= 400) {
      return null
    }
    const pendingNps = unwrapPodoBaseResponse(body)

    if (!isNpsPendingResponse(pendingNps)) {
      return null
    }

    return pendingNps
  })
}

export const getClassNpsReviewState = async (bearerToken: string, classId: number) => {
  try {
    return await withNpsRequestTimeout(async (signal) => {
      const response = await authenticatedFetch(createPodoApiUrl(`/api/v1/lesson-review/nps/classes/${classId}`), {
        method: 'GET',
        headers: createJsonHeaders(bearerToken),
        signal,
        cache: 'no-store',
      })
      if (!response.ok) {
        return null
      }
      const body: unknown = await response.json()
      if (isRecord(body) && Number(body.resultCd) >= 400) {
        return null
      }
      const parsed = npsReviewStateSchema.safeParse(unwrapPodoBaseResponse(body))
      return parsed.success && parsed.data.classId === classId ? parsed.data : null
    }, 3_000)
  } catch {
    return null
  }
}

export const skipNps = async (bearerToken: string, payload: NpsSkipPayload): Promise<NpsSubmitResponse> => {
  return await withNpsRequestTimeout(async (signal) => {
    const response = await authenticatedFetch(createPodoApiUrl('/api/v1/lesson-review/nps/skip'), {
      method: 'POST',
      headers: createJsonHeaders(bearerToken),
      body: JSON.stringify(payload),
      signal,
    })

    if (!response.ok) {
      throw new Error(`Failed to skip NPS: ${response.status}`)
    }

    const body: unknown = await response.json()
    if (isRecord(body) && Number(body.resultCd) >= 400) {
      throw new Error('NPS skip was rejected')
    }
    const saved = unwrapPodoBaseResponse(body)
    if (!isRecord(saved) || saved.success !== true) {
      throw new Error('NPS skip was not saved')
    }
    return { success: true }
  })
}
